import type { FeeBasis, RelatedDrug } from '@/entities/drug/api/drug.api';
import { DrugBadges } from '@/entities/drug/components/DrugBadges';
import { DrugMeta } from '@/entities/drug/components/DrugMeta';
import { useRelatedDrugs } from '@/entities/drug/hooks/useDrugQueries';
import {
  drugKindLabels,
  formatFeeRate,
  isLinkableDrugCode,
} from '@/entities/drug/utils/drug.utils';
import { ErrorState } from '@/shared/components/StateViews';
import { Button } from '@/shared/components/ui/button';
import { CheckboxField } from '@/shared/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { HelpText } from '@/shared/components/ui/help-text';
import { Icon } from '@/shared/components/ui/icon';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { cn } from '@/shared/lib/utils';
import { EMPTY, formatWon } from '@/shared/utils/format';
import { useUIStore } from '@/stores/uiStore';
import { Fragment, useMemo, useState, type ReactNode } from 'react';

const BASIS_OPTIONS: { value: FeeBasis; label: string }[] = [
  { value: 'PHARMA', label: '제약사 기준' },
  { value: 'USER', label: '사용자 기준' },
];

/**
 * 동일성분 목록 정렬 (기획↔디자인 변경 912:6416 "동일성분 목록 정렬 선택" · Figma 859:10389)
 * 서버(/related)는 예상액↓ 순으로만 주므로 받은 목록을 화면에서 다시 정렬한다. 값이 없는(null) 행은 항상 뒤로.
 */
const RELATED_SORTS = {
  EXPECTED_DESC: '예상 금액 높은순',
  FEE_RATE_DESC: '수수료율 높은순',
  PRICE_ASC: '약가 낮은순',
  NAME_ASC: '의약품 이름순',
} as const;
type RelatedSort = keyof typeof RELATED_SORTS;

const nullsLast = (a: number | null, b: number | null, dir: 'asc' | 'desc') => {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return dir === 'asc' ? a - b : b - a;
};

const byName = (a: RelatedDrug, b: RelatedDrug) =>
  a.drug.drugName.localeCompare(b.drug.drugName, 'ko');

const sortRelated = (rows: RelatedDrug[], sort: RelatedSort) =>
  [...rows].sort((a, b) => {
    switch (sort) {
      case 'FEE_RATE_DESC':
        return nullsLast(a.feeRate, b.feeRate, 'desc') || byName(a, b);
      case 'PRICE_ASC':
        return nullsLast(a.drug.price, b.drug.price, 'asc') || byName(a, b);
      case 'NAME_ASC':
        return byName(a, b);
      default:
        return (
          nullsLast(a.estimatedAmount, b.estimatedAmount, 'desc') ||
          byName(a, b)
        );
    }
  });

/** 차액 — 위로(+) 빨강 · 0 회색 · 아래로(-) 파랑 (Figma 833:7416 · 833:7159) */
const formatDiff = (diff: number) =>
  `(${diff > 0 ? '+' : diff < 0 ? '-' : ''}${Math.abs(diff).toLocaleString('ko-KR')}원)`;

/**
 * M-12 동일성분 의약품 탭 — 기준 토글(Tab Round) · 동일제형 군 포함 · 정렬 + 검색_결과_상세 카드(833:7127) 목록.
 * GET /related가 현재 약(첫 행, current)을 포함해 주지만, v1.0 디자인은 현재 약을 위 핵심 수치로 보여 주고
 * 목록에는 비교 대상만 둔다. 수수료율 · 예상액 · 차액(후보 − 현재)은 모두 선택한 기준의 서버 값이다.
 */
export const RelatedDrugsTab = ({
  drugCode,
  onSelect,
}: {
  drugCode: string;
  onSelect: (drugCode: string) => void;
}) => {
  const [basis, setBasis] = useState<FeeBasis>('PHARMA');
  const [includeGroup, setIncludeGroup] = useState(false);
  const [sort, setSort] = useState<RelatedSort>('EXPECTED_DESC');
  const { data, isPending, isError, isFetching, refetch } = useRelatedDrugs(
    drugCode,
    basis,
    includeGroup,
  );

  const rows = useMemo(
    () =>
      sortRelated(
        (data ?? []).filter((r) => !r.current),
        sort,
      ),
    [data, sort],
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-4">
          <Tabs
            value={basis}
            onValueChange={(v) => setBasis(v as FeeBasis)}
            aria-label="수수료율 기준"
          >
            <TabsList
              variant="round"
              className="gap-4 rounded-16 bg-surface-white p-2"
            >
              {BASIS_OPTIONS.map((o) => (
                <TabsTrigger
                  key={o.value}
                  value={o.value}
                  className="rounded-12 px-4 data-[state=active]:shadow-100 data-[state=inactive]:px-1 data-[state=inactive]:text-fg-tertiary data-[state=inactive]:hover:bg-transparent data-[state=inactive]:hover:text-fg-secondary"
                >
                  {o.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <div className="flex items-center">
            <CheckboxField
              label="동일제형 군 포함"
              checked={includeGroup}
              onCheckedChange={(v) => setIncludeGroup(v === true)}
            />
            <HoverHelp label="동일제형 군 안내">
              성분이 같고 제형 군이 같은 품목까지 함께 보여줘요.
            </HoverHelp>
          </div>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="medium"
              aria-label={`동일성분 정렬: ${RELATED_SORTS[sort]}`}
              rightIcon={<Icon name="nav-arrow-down-line-24" />}
            >
              {RELATED_SORTS[sort]}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {(Object.keys(RELATED_SORTS) as RelatedSort[]).map((k) => (
              <DropdownMenuItem
                key={k}
                selected={k === sort}
                onSelect={() => setSort(k)}
              >
                {RELATED_SORTS[k]}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {isPending ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-[156px] rounded-20 bg-grey-200" />
          ))}
        </div>
      ) : isError ? (
        <div className="rounded-20 bg-surface-white">
          <ErrorState onRetry={() => void refetch()} />
        </div>
      ) : rows.length === 0 ? (
        <p className="rounded-20 bg-surface-white py-12 text-center text-body-15-r text-fg-light">
          동일성분 의약품이 없어요.
        </p>
      ) : (
        <ul
          aria-label={`동일성분 의약품 ${rows.length}건`}
          className={cn(
            'flex flex-col gap-3 transition-opacity',
            isFetching && 'opacity-60',
          )}
        >
          {rows.map((r) => (
            <RelatedCard
              key={r.drug.drugCode}
              row={r}
              // 의약품 정보가 없는 품목(코드 "-" · ru…)은 상세를 열 수 없다
              onSelect={
                isLinkableDrugCode(r.drug.drugCode)
                  ? () => onSelect(r.drug.drugCode)
                  : undefined
              }
            />
          ))}
        </ul>
      )}
    </div>
  );
};

/**
 * 검색_결과_상세 (833:7127) — 흰 카드 r20 · p16 · 간격 16
 * 위: 품절 배지 / 약명 16 Bold / 대조약 · 제약사 + [필터링 신청](Small · 앱 전용이라 비활성 모양)
 * 아래: 회색 r12 — 약가 · 수수료율 · 예상 금액(차액)
 * 거래 불가(제약사 약품 코드 없음) 품목은 수치를 "—"로 두고 차액 자리에 "거래 불가"를 쓴다.
 */
const RelatedCard = ({
  row,
  onSelect,
}: {
  row: RelatedDrug;
  onSelect?: () => void;
}) => {
  const openAppOnly = useUIStore((s) => s.openAppOnly);
  const { drug, feeRate, estimatedAmount, differenceAmount: diff } = row;
  // 필터링 신청은 거래 가능하고 품절 · 신규 필터링 금지가 아닌 품목에만 (Figma 버튼 prop: 품절 카드는 숨김)
  const canApply = drug.tradable && !drug.outOfStock && !drug.newDrugBanned;

  return (
    <li
      onClick={onSelect}
      onKeyDown={(e) => {
        if (
          onSelect &&
          e.target === e.currentTarget &&
          (e.key === 'Enter' || e.key === ' ')
        ) {
          e.preventDefault();
          onSelect();
        }
      }}
      role={onSelect ? 'button' : undefined}
      tabIndex={onSelect ? 0 : undefined}
      aria-label={onSelect ? `${drug.drugName} 상세 보기` : undefined}
      className={cn(
        'flex flex-col gap-4 rounded-20 bg-surface-white p-4 outline-1 -outline-offset-1 outline-transparent transition-colors focus-visible:ring-3 focus-visible:ring-brand-200',
        onSelect && 'cursor-pointer hover:outline-grey-900',
      )}
    >
      <div className="flex items-start gap-4">
        <div className="flex min-w-0 flex-1 flex-col items-start gap-2">
          <DrugBadges
            outOfStock={drug.outOfStock}
            newDrugBanned={drug.newDrugBanned}
          />
          <p className="text-subtitle-16-b text-fg-primary">{drug.drugName}</p>
          <DrugMeta
            className="text-body-15-r text-fg-tertiary"
            items={[
              ...drugKindLabels(drug),
              drug.pharmaCompanyName,
              row.sameFormulationGroup && '동일제형 군',
            ]}
          />
        </div>
        {canApply && (
          <Button
            size="small"
            // 앱 전용 기능 — 비활성 모양(Filled/default Disabled)이지만 눌러서 앱 안내 팝업(X6)을 연다
            aria-description="앱에서만 신청할 수 있어요"
            className="bg-grey-200 text-fg-disabled hover:bg-grey-200 active:bg-grey-200"
            onClick={(e) => {
              e.stopPropagation();
              openAppOnly('filteringApply');
            }}
            onKeyDown={(e) => e.stopPropagation()}
          >
            필터링 신청
          </Button>
        )}
      </div>

      <dl className="flex items-stretch rounded-12 bg-surface-grey pt-4 pb-3">
        <Cell label="약가">
          <span className="text-body-15-sb text-fg-primary">
            {formatWon(drug.price)}
          </span>
        </Cell>
        <span aria-hidden className="w-px bg-line-grey" />
        <Cell label="수수료율">
          <span
            className={cn(
              'text-body-15-sb',
              feeRate == null ? 'text-fg-light' : 'text-fg-info',
            )}
          >
            {feeRate == null ? EMPTY : formatFeeRate(feeRate)}
          </span>
        </Cell>
        <span aria-hidden className="w-px bg-line-grey" />
        <Cell label="100회 처방 예상 금액">
          <span
            className={cn(
              'text-body-15-sb',
              estimatedAmount == null ? 'text-fg-light' : 'text-fg-primary',
            )}
          >
            {estimatedAmount == null ? EMPTY : formatWon(estimatedAmount)}
          </span>
          {!drug.tradable ? (
            <span className="text-body-13-m text-fg-tertiary">거래 불가</span>
          ) : (
            diff != null && (
              <span
                className={cn(
                  'text-body-13-m',
                  diff > 0
                    ? 'text-fg-error'
                    : diff < 0
                      ? 'text-fg-accent'
                      : 'text-fg-tertiary',
                )}
              >
                {formatDiff(diff)}
              </span>
            )
          )}
        </Cell>
      </dl>
    </li>
  );
};

/** 카드 아래 수치 칸 — Figma는 라벨 없이 값만 보여 주므로 라벨은 스크린 리더용으로만 둔다 */
const Cell = ({ label, children }: { label: string; children: ReactNode }) => (
  <Fragment>
    <dt className="sr-only">{label}</dt>
    <dd className="flex min-w-0 flex-1 flex-wrap items-center justify-center gap-1 text-center">
      {children}
    </dd>
  </Fragment>
);

/** ⓘ 도움말 — help-circle 14 버튼(24)에 호버 · 포커스하면 검정 helptext를 띄운다 (GNB Maxmatch 가이드와 같은 방식) */
const HoverHelp = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        aria-label={label}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="flex size-6 items-center justify-center rounded-8 text-fg-light outline-none focus-visible:ring-3 focus-visible:ring-brand-100"
      >
        <Icon name="help-circle-solid-14" />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        collisionPadding={16}
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="w-auto rounded-20 border-0 bg-transparent p-0 shadow-500"
      >
        <HelpText tone="dark" icon={null} className="whitespace-nowrap">
          {children}
        </HelpText>
      </PopoverContent>
    </Popover>
  );
};
